import { useContext, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useAuthContext } from "../../../contexts/AuthContext";
import { AppContext } from "../../../contexts/AppContext";
import { EditAgentSidebar } from "./EditAgentSidebar";
import { WorkflowGraph } from "./WorkflowGraph";
import { DynamicWorkflowGraphTab } from "../../../components/Workflows/DynamicWorkflowGraphTab";
import { ChatSessions } from "./ChatSessions";
import CoreDetails from "./CoreDetails";
import { BackLink, Button, CancelButton, PageTitle, PageHeader } from "../../../components/StyledElements";
import { getAgent } from "../../../services/agents"
import Loading from "../../../components/Loading";
import swal from "sweetalert2";

import { useAgents } from "../../../contexts/AgentsContext";
import { resolveGeneratorClientId } from "../../../utils/workflowGenerationRequest";

export const EditAgent = () => {
  const { activeUser } = useAuthContext();
  const { selectedClient } = useContext(AppContext);
  const navigate = useNavigate();
  const { agent_id } = useParams();
  const [showSidebar, setShowSidebar] = useState(true);
  const [selectedTab, setSelectedTab] = useState(window.location.hash ? window.location.hash.substr(1) : "details");

  const { selectedAgent, setSelectedAgent, setUpdatedAgent, updatedAgent, canSaveAgent, saveUpdatedAgent, getDefaultWorkflow, setSelectedChatSession, selectedChatSession } = useAgents();
  const [savingAgent, setSavingAgent] = useState(false);

  console.log("!!! AGENT ID: ", agent_id);

  useEffect(() => {
    // Fetch live event details using agent_id
    const fetchAgent = async () => {
      const results = await getAgent(agent_id, true);
      console.log("!!! AGENT RESULTS: ", results);
      if (results.success) {
        const agent = results.agent;
        if (agent.workflow_graph && typeof agent.workflow_graph === "string") {
          console.log("!!! PARSING WORKFLOW GRAPH: ", agent.workflow_graph);
          agent.workflow_graph = JSON.parse(agent.workflow_graph);
        } else {
          agent.workflow_graph = getDefaultWorkflow();
        }
        setUpdatedAgent(agent);
        setSelectedAgent(agent);
      } else {
        swal.fire({
          title: "Error",
          text: results.message || "Failed to fetch agent details.",
          icon: "error",
        }).then(() => {
          navigate(`/agents/`);
        });
      }
    };
    if (agent_id) {
      fetchAgent();
    } else {
      const newAgent = {
        public_id: "",
        name: "",
        workflow_graph: getDefaultWorkflow(),
        description: "",
        purpose: "",
        client_id: activeUser?.client_id || null,
      };
      setUpdatedAgent(newAgent);
      setSelectedAgent(newAgent);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [agent_id]);

  const saveAgent = async () => {
    if (!canSaveAgent() || savingAgent) {
      return;
    }
    setSavingAgent(true);
    const public_id = await saveUpdatedAgent();
    console.log("!!! PUBLIC ID AFTER SAVE: ", public_id);
    if (public_id) {
      // Update url if it's a new event
      window.history.replaceState(null, null, `/agent/${public_id}`);
      swal.fire({
        title: "Success",
        text: "Agent saved successfully.",
        icon: "success",
      });
    }
    setSavingAgent(false);
  }

  if (!updatedAgent && agent_id) {
    return <Loading>Loading Agent...</Loading>;
  }

  return (
    <div style={{ display: "flex", flexDirection: "row", backgroundColor: "var(--main-bg)", height: "100%" }}>
      <div style={{ display: "flex", flexDirection: "row", backgroundColor: "var(--main-bg)" }}>

        <EditAgentSidebar
          show={showSidebar}
          defaultSelectedTab={selectedTab}
          onNavItemClicked={(navItem) => {
            setSelectedTab(navItem);
          }}
          onToggle={() => {
            setShowSidebar(!showSidebar);
          }}
        />
      </div>
      <div
        style={{
          marginLeft: "20px",
          marginRight: "20px",
          width: `calc(100% - ${showSidebar ? "250px" : "0px"})`,
          minHeight: "calc(100vh - 40px)",
          position: "relative",
          backgroundColor: "var(--content-item-bg)",
          padding: "20px",
          borderRadius: "8px",
          color: "var(--main-font-color)",
          zIndex: 1,
          /*height: "2000px",*/
        }}
      >
        <PageHeader style={{ position: "sticky", top: "0px", backgroundColor: "var(--content-item-bg)", zIndex: 2, display: "flex", flexDirection: "row", gap: "10px", alignItems: "center" }}>
          <div>
            <BackLink onClick={() => {
              navigate(`/agents/#agents-list`);
            }} >
              <i className="fa fa-arrow-left" /> Back to Agents</BackLink>
            <PageTitle title={`${updatedAgent?.id ? "Edit" : "New"} Agent`} style={{ marginBottom: "0px" }} />
            {updatedAgent?.title ?
              <PageTitle title={updatedAgent?.title} style={{ marginBottom: "10px", fontWeight: "500", fontSize: "20px" }} />
              : null}
          </div>
          {selectedTab !== "chat-sessions" ? (
            <div style={{ marginLeft: "auto", display: "flex", flexDirection: "row", gap: "10px" }}>
              <CancelButton onClick={() => {
                setUpdatedAgent(selectedAgent);
              }}>Cancel</CancelButton>
              <Button disabled={!canSaveAgent() || savingAgent} onClick={saveAgent} style={{ marginLeft: "auto" }}>{savingAgent ? "Saving Changes" : "Save Changes"}</Button>
            </div>
          ) : (
            <>
              {selectedChatSession ? (
                <div style={{ marginLeft: "auto", display: "flex", flexDirection: "row", gap: "10px" }}>
                  <Button onClick={() => {
                    setSelectedChatSession(null);
                  }} style={{ marginLeft: "auto" }}>Close Chat Session</Button>
                </div>
              ) : null}
            </>
          )}
        </PageHeader>
        <div style={{ backgroundColor: "var(--content-item-bg)", marginTop: "20px", marginBottom: "40px", height: "calc(100% - 80px)" }}>
          <div style={{ display: "flex", flexDirection: "column", gap: "20px", height: "100%" }}>
            <div style={{ width: "100%", height: "100%" }}>
              <CoreDetails
                style={{ display: selectedTab === "details" ? "flex" : "none" }}
                onSelectTab={(tab) => {
                  window.history.replaceState(null, null, `#${tab}`);
                  setSelectedTab(tab);
                }} />
              {selectedTab === "workflow-graph" && (
                <WorkflowGraph style={{ display: "flex" }}
                  onSelectTab={(tab) => {
                    window.history.replaceState(null, null, `#${tab}`);
                    setSelectedTab(tab);
                  }} />
              )}
              {selectedTab === "dynamic-workflow" && (
                <DynamicWorkflowGraphTab
                  graph={updatedAgent?.workflow_graph}
                  onGraphChange={(graph) => {
                    setUpdatedAgent({ ...updatedAgent, workflow_graph: graph });
                  }}
                  generate={{
                    kind: "agent",
                    defaultName: updatedAgent?.name ? `${updatedAgent.name} workflow` : "",
                    defaultDescription: updatedAgent?.description ?? "",
                    defaultPurpose: updatedAgent?.purpose ?? "",
                    clientId: resolveGeneratorClientId(selectedClient, updatedAgent),
                  }}
                  run={{ agent: updatedAgent }}
                />
              )}
              <ChatSessions style={{ display: selectedTab === "chat-sessions" ? "flex" : "none" }}
                onSelectTab={(tab) => {
                  window.history.replaceState(null, null, `#${tab}`);
                  setSelectedTab(tab);
                }} />

            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default EditAgent;

